import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarBlank, X } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";

const PREFIX = { Template: "template", Fabrication: "fab", Install: "install" };
const WINDOWS = ["All day", "AM", "PM"];

// Legacy data stores times like "8:30 AM"; a native <input type="time"> needs "HH:MM".
const to24h = (raw) => {
  const v = (raw || "").trim();
  if (!v) return "";
  if (/^\d{1,2}:\d{2}$/.test(v)) {
    const [h, m] = v.split(":");
    return `${String(Math.min(23, +h)).padStart(2, "0")}:${m}`;
  }
  const m = v.match(/^(\d{1,2}):(\d{2})\s*([AaPp][Mm])$/);
  if (m) {
    let h = +m[1] % 12;
    if (/[Pp]/.test(m[3])) h += 12;
    return `${String(h).padStart(2, "0")}:${m[2]}`;
  }
  return "";
};

export const RescheduleSheet = ({ open, onOpenChange, jobId, kind, current, onDone }) => {
  const [date, setDate] = useState("");
  const [win, setWin] = useState("All day");
  const [time, setTime] = useState("");
  const [notify, setNotify] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setDate(current?.date || "");
      setWin(current?.window || "All day");
      setTime(to24h(current?.time));
      setNotify(false);
    }
  }, [open, current?.date, current?.window, current?.time]);

  if (!open) return null;
  const prefix = PREFIX[kind];

  const save = async () => {
    if (!prefix) {
      toast.error("This task can't be rescheduled");
      return;
    }
    if (!date) {
      toast.error("Pick a date");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.patch(`/jobs/${jobId}`, {
        [`${prefix}_date`]: date,
        [`${prefix}_window`]: win,
        [`${prefix}_time`]: time || "",
      });
      if (notify) {
        try {
          const { data: n } = await api.post(`/jobs/${jobId}/reschedule-notify`, { kind });
          if (n.status === "failed" || n.status === "skipped") {
            toast.success(`${kind} rescheduled`, {
              description: `Text not sent: ${n.error || n.status}`,
            });
          } else {
            toast.success(`${kind} rescheduled — customer texted`);
          }
        } catch (e) {
          toast.success(`${kind} rescheduled`, {
            description: "Couldn't text the customer",
          });
        }
      } else {
        toast.success(`${kind} rescheduled`);
      }
      onOpenChange(false);
      onDone?.(data);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Could not reschedule",
      );
    }
    setBusy(false);
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end justify-center"
      data-testid="reschedule-sheet"
    >
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={() => onOpenChange(false)}
      />
      <div className="relative z-10 max-h-[88vh] w-full max-w-md overflow-y-auto rounded-t-3xl border-t border-zinc-800 bg-zinc-950 p-5 pb-24">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2 text-[13px] font-bold uppercase tracking-widest text-indigo-300">
            <CalendarBlank size={18} weight="bold" /> Reschedule {kind?.toLowerCase()}
          </div>
          <button
            type="button"
            data-testid="reschedule-close"
            onClick={() => onOpenChange(false)}
            className="flex h-8 w-8 items-center justify-center rounded-lg border border-zinc-800 text-zinc-400 hover:text-zinc-200"
          >
            <X size={15} weight="bold" />
          </button>
        </div>

        <label className="block">
          <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
            Date
          </span>
          <input
            type="date"
            data-testid="reschedule-date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="mt-1 h-12 w-full rounded-xl border border-zinc-800 bg-zinc-900 px-3 text-base text-zinc-100 focus:border-indigo-500 focus:outline-none"
          />
        </label>

        <div className="mt-4">
          <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
            Arrival window
          </span>
          <div className="mt-1 grid grid-cols-3 gap-2">
            {WINDOWS.map((w) => (
              <button
                key={w}
                type="button"
                data-testid={`reschedule-window-${w}`}
                onClick={() => setWin(w)}
                className={`h-11 rounded-xl border text-[12px] font-bold uppercase tracking-widest transition-colors duration-150 ${
                  win === w
                    ? "border-indigo-500 bg-indigo-500/15 text-indigo-200"
                    : "border-zinc-800 bg-zinc-900 text-zinc-400 hover:bg-zinc-800"
                }`}
              >
                {w}
              </button>
            ))}
          </div>
        </div>

        <label className="mt-4 block">
          <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
            Specific time (optional)
          </span>
          <input
            type="time"
            data-testid="reschedule-time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            className="mt-1 h-12 w-full rounded-xl border border-zinc-800 bg-zinc-900 px-3 text-base text-zinc-100 focus:border-indigo-500 focus:outline-none"
          />
        </label>

        <label className="mt-4 flex items-center gap-3 rounded-xl border border-zinc-800 bg-zinc-900 p-3">
          <input
            type="checkbox"
            data-testid="reschedule-notify"
            checked={notify}
            onChange={(e) => setNotify(e.target.checked)}
            className="h-4 w-4 accent-indigo-500"
          />
          <span className="text-[12px] text-zinc-300">
            Text the customer the new date &amp; time
          </span>
        </label>

        <div className="mt-6 flex gap-2">
          <button
            type="button"
            data-testid="reschedule-cancel"
            onClick={() => onOpenChange(false)}
            className="h-12 flex-1 rounded-xl border border-zinc-700 text-[12px] font-bold uppercase tracking-widest text-zinc-300 hover:bg-zinc-800"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="reschedule-save"
            onClick={save}
            disabled={busy || !date}
            className="h-12 flex-1 rounded-xl bg-indigo-500 text-[12px] font-bold uppercase tracking-widest text-white hover:bg-indigo-400 disabled:opacity-40"
          >
            {busy ? "Saving…" : "Save new date"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RescheduleSheet;
